(function () {
  "use strict";

  // Display never depends on this file — every video already has a poster
  // (or, for a direct-embed Vimeo fallback, its own iframe) rendered
  // directly by the section's Liquid. This script adds three purely
  // additive layers: click-to-play (poster -> real <video>/<iframe> on
  // click), impression/open/watch-time analytics syncing into the
  // LiteReels admin app, and the carousel controls (arrows, scrollbar,
  // edge fade) for rows that overflow their configured column count. If
  // any of it fails (blocked script, network hiccup, ad blocker), the
  // gallery still displays and the native scroll/swipe still works.
  //
  // Declared before the init calls below: initGallery runs synchronously
  // from here, so anything it touches must already be initialized.
  const rowNavigators = new WeakMap(); // grid element -> (item) => void (see initRowPosition)

  document.querySelectorAll("[data-litelok-gallery]").forEach(initGallery);
  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector("[data-litelok-gallery]");
    if (root) initGallery(root);
  });

  function sendEvent(apiBase, videoId, type, sectionId, store, value) {
    const payload = JSON.stringify({ videoId, type, placementHandle: sectionId, placementType: "gallery", store, value });
    const url = `${apiBase}/api/analytics/event`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
    }
  }

  // ---- Playback-session tracking (shared with video-reels.js — keep the
  // two copies identical) ----
  //
  // One session per play of one video in this page view, reported to
  // /api/analytics/playback (see the VideoView model in the admin app).
  // Records every second of the video actually played — a second counts
  // once playback crosses its midpoint, so seeking past it doesn't — plus
  // exactly where and why playback stopped, pauses, seeks, loops, sound
  // and fullscreen. Each beat carries the whole session so far with an
  // increasing `seq`; the server keeps the newest, so a lost or reordered
  // beacon never loses or double-counts anything. Beats go every few
  // seconds while playing and immediately on every stop.
  //
  // Replaces the old "watch_time"/"watch_complete" events, which sent a
  // running tally in 5s chunks: every chunk was counted as a separate
  // "view" and the last <5s of every watch was never sent.
  const PAGE_VIEW_ID = randomId();
  const liveTrackers = new Set();
  const trackerByVideo = new WeakMap();

  function randomId() {
    const bytes = new Uint8Array(12);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }

  function deviceType() {
    if (!window.matchMedia("(pointer: coarse)").matches) return "desktop";
    return Math.min(window.screen.width, window.screen.height) < 600 ? "mobile" : "tablet";
  }

  function postBeacon(url, payload) {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  }

  // Why the next pause is happening, when our own code is the one pausing
  // (scrolled away, switched video, closed) — otherwise it's the viewer.
  function notePauseReason(video, reason) {
    const tracker = trackerByVideo.get(video);
    if (tracker && !video.paused) tracker.pauseReason = reason;
  }

  function trackPlayback(video, opts) {
    const HEARTBEAT_MS = 5000;
    const previous = trackerByVideo.get(video);
    if (previous) previous.end();

    const viewId = randomId();
    const device = deviceType();
    const plays = []; // plays[n] = times second n of the video has been played
    let seq = 0;
    let lastTime = video.currentTime || 0;
    let maxPosition = lastTime;
    let playing = false;
    let started = false;
    let pauses = 0;
    let seeksBack = 0;
    let seeksForward = 0;
    let lastSeekAt = 0;
    let loops = 0;
    let completed = false;
    let soundOn = false;
    let fullscreen = false;
    let exitReason = null;
    let timer = null;
    const tracker = { pauseReason: null, flush, end };

    function countPlayed(from, to) {
      for (let i = Math.floor(from - 0.5) + 1; i + 0.5 <= to; i++) {
        if (i < 0) continue;
        while (plays.length <= i) plays.push(0);
        plays[i]++;
      }
    }

    function advance() {
      const t = video.currentTime;
      const d = t - lastTime;
      if (playing && d > 0 && d < 4) countPlayed(lastTime, t);
      lastTime = t;
      if (t > maxPosition) maxPosition = t;
      if (!video.muted && video.volume > 0 && playing) soundOn = true;
    }

    function runs() {
      const out = [];
      for (let i = 0; i < plays.length; i++) {
        if (!plays[i]) continue;
        const last = out[out.length - 1];
        if (last && last[0] + last[1] === i && last[2] === plays[i]) last[1]++;
        else out.push([i, 1, plays[i]]);
      }
      return out;
    }

    function flush() {
      if (!started) return;
      const videoId = opts.getVideoId();
      // Not known yet (the gallery's id sync hasn't come back) — keep
      // accumulating; the next beat carries everything so far anyway.
      if (!videoId || !opts.apiBase) return;
      seq++;
      postBeacon(`${opts.apiBase}/api/analytics/playback`, {
        viewId,
        pageViewId: PAGE_VIEW_ID,
        videoId,
        placementId: opts.getPlacementId() || undefined,
        surface: opts.surface,
        store: opts.store,
        device,
        seq,
        duration: isFinite(video.duration) ? +video.duration.toFixed(2) : 0,
        position: +lastTime.toFixed(2),
        maxPosition: +maxPosition.toFixed(2),
        runs: runs(),
        pauses,
        seeksBack,
        seeksForward,
        loops,
        completed,
        soundOn,
        fullscreen,
        exitReason: exitReason || undefined,
      });
    }

    function stopHeartbeat() {
      clearInterval(timer);
      timer = null;
    }

    const on = {
      play() {
        started = true;
        playing = true;
        exitReason = null;
        tracker.pauseReason = null;
        lastTime = video.currentTime;
        if (!timer) timer = setInterval(flush, HEARTBEAT_MS);
      },
      timeupdate: advance,
      seeking() {
        const to = video.currentTime;
        // A looping video (Reels) jumps back to the start by itself — that's
        // a completed play-through, not the viewer seeking.
        if (video.loop && isFinite(video.duration) && lastTime >= video.duration - 1.5 && to < 1) {
          if (playing) countPlayed(lastTime, video.duration);
          loops++;
          completed = true;
        } else if (Math.abs(to - lastTime) >= 1) {
          // One drag along a seek bar fires many seeks — count it once.
          const now = Date.now();
          if (now - lastSeekAt > 700) {
            if (to < lastTime) seeksBack++;
            else seeksForward++;
          }
          lastSeekAt = now;
        }
        lastTime = to;
      },
      pause() {
        if (video.ended) return; // "ended" reports this one
        advance();
        playing = false;
        stopHeartbeat();
        exitReason = tracker.pauseReason || (document.visibilityState === "hidden" ? "left_page" : "paused");
        if (exitReason === "paused") pauses++;
        tracker.pauseReason = null;
        flush();
      },
      ended() {
        advance();
        if (isFinite(video.duration)) countPlayed(lastTime, video.duration);
        playing = false;
        stopHeartbeat();
        completed = true;
        exitReason = "ended";
        flush();
      },
      volumechange() {
        if (!video.muted && video.volume > 0 && playing) soundOn = true;
      },
      webkitbeginfullscreen() {
        fullscreen = true;
      },
    };
    function onFullscreenChange() {
      const el = document.fullscreenElement || document.webkitFullscreenElement;
      if (el && (el === video || el.contains(video))) fullscreen = true;
    }

    // Page going away (tab closed, navigated, app switched): report where
    // things stand. If it was still playing, that's where they left.
    tracker.leavePage = () => {
      if (playing) {
        advance();
        exitReason = "left_page";
      }
      flush();
    };

    function end() {
      stopHeartbeat();
      Object.keys(on).forEach((type) => video.removeEventListener(type, on[type]));
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      liveTrackers.delete(tracker);
      if (trackerByVideo.get(video) === tracker) trackerByVideo.delete(video);
    }

    Object.keys(on).forEach((type) => video.addEventListener(type, on[type]));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    liveTrackers.add(tracker);
    trackerByVideo.set(video, tracker);
    if (!video.paused) on.play();
    return tracker;
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") liveTrackers.forEach((t) => t.flush());
  });
  window.addEventListener("pagehide", () => liveTrackers.forEach((t) => t.leavePage()));

  // Wires playback-session tracking onto a native <video> (see
  // trackPlayback). YouTube/Vimeo embeds can't report playback without
  // each platform's player SDK, so those only ever get the "open" event.
  function wireAnalytics(item, mediaEl, kind, ctx) {
    if (kind !== "video") return;
    trackPlayback(mediaEl, {
      apiBase: ctx.apiBase,
      surface: "gallery",
      store: ctx.store,
      getVideoId: ctx.getVideoId,
      getPlacementId: () => item.closest("[data-litelok-gallery]")?.dataset.placementId,
    });
  }

  function markOpen(item, ctx) {
    if (item.dataset.opened) return;
    item.dataset.opened = "true";
    const videoId = item.dataset.videoId;
    if (videoId) sendEvent(ctx.apiBase, videoId, "open", ctx.sectionId, ctx.store);
  }

  // Tracks "the user explicitly went fullscreen on this row" as its own
  // fact, separate from whatever document.fullscreenElement happens to say
  // at any given instant — see the comment on fsIntentFor below for why
  // that distinction actually matters here.
  const fsIntent = new WeakMap(); // root element -> "video" | "container"

  function fsIntentFor(item) {
    return item.closest("[data-litelok-gallery]");
  }

  // Fullscreen — two different targets depending on viewport, because
  // mobile and desktop fullscreen aren't the same problem:
  //
  // Desktop: fullscreens the card container (.litelok-gallery__media), not
  // the bare <video>, since a video element can't have visible HTML
  // children — a "next video" button placed inside it would never render
  // once fullscreen starts, only the fullscreened element's own
  // descendants stay on screen. There's no orientation concern on desktop,
  // so this trade-off (custom container, custom next button) is free.
  //
  // Mobile: fullscreens the actual <video> element via the browser's own
  // native video fullscreen instead. iOS/Android give every <video> a
  // built-in fullscreen mode that auto-rotates to landscape and fills the
  // screen properly (this is literally what YouTube's own mobile web
  // player relies on) — a generic container fullscreen doesn't get that
  // special orientation handling, which is why it looked cramped/cut-off
  // rather than filling the screen like a native video player does. The
  // trade-off is losing the custom "next" button overlay on mobile (a
  // video element can't host it) — an acceptable one here, since the
  // auto-advance/next-up countdown still works regardless of fullscreen.
  //
  // 750px matches the same breakpoint the rest of this section already
  // uses for mobile vs desktop layout.
  function addFullscreenButton(media, video, item) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "litelok-gallery__fullscreen";
    btn.setAttribute("aria-label", "Fullscreen");
    btn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5V1H5M11 1H15V5M15 11V15H11M5 15H1V11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      // The button never checked whether it was already fullscreen — every
      // click just re-requested fullscreen (a no-op once already in it),
      // so the button looked broken as an exit control on desktop and the
      // only way out was the browser's own Esc key. Toggle instead.
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) return void document.exitFullscreen().catch(() => {});
        if (document.webkitExitFullscreen) return void document.webkitExitFullscreen();
      }
      const root = fsIntentFor(item);
      const isMobile = window.innerWidth < 750;
      if (isMobile && video) {
        // Our touch controls sit on top of the <video>, so they can't
        // follow it into native fullscreen: hand over to the browser's own
        // controls there (iOS uses its own in fullscreen regardless), and
        // take them back on exit (handleFsExit below).
        if (media.dataset.touchControls) video.controls = true;
        if (video.requestFullscreen) {
          fsIntent.set(root, "video");
          return void video.requestFullscreen().catch(() => {
            if (media.dataset.touchControls) video.controls = false;
          });
        }
        if (video.webkitEnterFullscreen) {
          fsIntent.set(root, "video");
          return void video.webkitEnterFullscreen();
        }
      }
      if (media.requestFullscreen) {
        fsIntent.set(root, "container");
        media.requestFullscreen();
      } else if (media.webkitRequestFullscreen) {
        fsIntent.set(root, "container");
        media.webkitRequestFullscreen();
      } else if (video && video.webkitEnterFullscreen) {
        fsIntent.set(root, "video");
        video.webkitEnterFullscreen();
      }
    });
    media.appendChild(btn);

    // iOS fires webkitendfullscreen on the <video> itself the instant its
    // native fullscreen closes — including the moment the video reaches
    // its own end, which happens automatically on many mobile browsers
    // *before* (or as) the "ended" handler runs. Without this distinction,
    // that auto-exit looked identical to the user deliberately backing out
    // of fullscreen, and clearing the intent flag on every exit meant
    // end-of-video auto-advance could never tell it had been fullscreen a
    // moment ago. Only clear it here when the video *isn't* at/near its
    // end — a genuine manual exit mid-playback — so natural end-of-video
    // exits leave the flag alone for the "ended" handler (see goToNextItem)
    // to act on. video-level fullscreen only; desktop's container
    // fullscreen doesn't auto-exit like this, so it doesn't need this.
    function handleFsExit() {
      const stillFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
      if (stillFullscreen) return;
      if (media.dataset.touchControls) video.controls = false;
      const nearEnd = video.duration && video.duration - video.currentTime < 0.75;
      if (!video.ended && !nearEnd) fsIntent.delete(fsIntentFor(item));
    }
    video.addEventListener("webkitendfullscreen", handleFsExit);
    document.addEventListener("fullscreenchange", handleFsExit);
  }

  // Fullscreen for YouTube/Vimeo embeds — a separate, simpler path from
  // addFullscreenButton above: an iframe has no native "video-level"
  // fullscreen API to reach for, and no orientation-auto-rotate benefit to
  // chase on mobile either (that was specifically a property of a bare
  // <video> element), so there's no dual mobile/desktop case here — just
  // fullscreen the card container, on every breakpoint, the same way
  // desktop does for uploaded videos. Added because YouTube/Vimeo's own
  // in-player fullscreen control doesn't reliably stay reachable at every
  // embedded card width across devices (reported directly: cut off/out of
  // view in some mobile previews) — this gives a fullscreen control that's
  // always in the same place regardless of what the embedded platform's
  // own UI does.
  function addIframeFullscreenButton(media, iframe) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "litelok-gallery__fullscreen";
    btn.setAttribute("aria-label", "Fullscreen");
    btn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5V1H5M11 1H15V5M15 11V15H11M5 15H1V11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) return void document.exitFullscreen().catch(() => {});
        if (document.webkitExitFullscreen) return void document.webkitExitFullscreen();
      }
      if (media.requestFullscreen) media.requestFullscreen().catch(() => {});
      else if (media.webkitRequestFullscreen) media.webkitRequestFullscreen();
    });
    media.appendChild(btn);
  }

  // Only one video (uploaded or embedded) actually playing at a time —
  // clicking a new item now stops whatever was previously playing rather
  // than letting multiple audio tracks run at once. Muted hover previews
  // (initHoverPreview/initMobilePreview) are untouched by this: they're a
  // different element entirely (.litelok-gallery__preview) and were never
  // real "playing" in this sense.
  //
  // Native <video> is a guaranteed pause — same-origin, fully scriptable.
  // YouTube/Vimeo iframes are cross-origin, so there's no direct .pause()
  // to call; postMessage is the only lever, and only works because
  // playItem's embed_src already requests each platform's player API
  // (enablejsapi=1 for YouTube, api=1 for Vimeo — see litelok-gallery.
  // liquid). Best-effort: if a platform ever changes this contract, the
  // old video silently keeps playing in the background rather than
  // throwing, same as any other postMessage call to a black-box iframe.
  function pauseOtherPlayingItems(root, exceptItem) {
    if (!root) return;
    root.querySelectorAll('.litelok-gallery__item[data-opened="true"]').forEach((other) => {
      if (other === exceptItem) return;
      const media = other.querySelector(".litelok-gallery__media");
      if (!media) return;
      const video = media.querySelector("video:not(.litelok-gallery__preview)");
      if (video && !video.paused) {
        notePauseReason(video, "switched_video");
        video.pause();
      }
      const iframe = media.querySelector("iframe");
      if (!iframe || !iframe.contentWindow) return;
      if (other.dataset.provider === "youtube") {
        iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
      } else if (other.dataset.provider === "vimeo") {
        iframe.contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*");
      }
    });
  }

  // Jumps straight to the next item in the row and plays it — used both by
  // the next-up countdown/"Play now" and the fullscreen "next" button.
  //
  // If the CURRENT item is the one actually in fullscreen, playItem() on
  // the next item still swaps in a brand new <video>/media element that
  // was never fullscreened — the browser keeps showing the old, now-ended
  // element on screen while the new one silently plays off-screen. That
  // made both the fullscreen "next" arrow and end-of-video auto-advance
  // look broken (nothing visibly changed). Fix: detect which element is
  // actually fullscreen before swapping, then re-request fullscreen on
  // its equivalent in the next item once it exists.
  //
  // Reads fsIntent (set when the user explicitly went fullscreen), not
  // document.fullscreenElement live at this instant — on mobile, a video
  // reaching its natural end often auto-exits native fullscreen *before*
  // this runs, so checking the live value here would already see "nothing
  // is fullscreen" and silently give up, leaving the next video playing
  // inline/off-screen instead of re-entering fullscreen for it. That's
  // exactly the bug this replaced: confirmed by testing — ending a video
  // while fullscreen on mobile left the viewer looking at the previous
  // video's (now-ended) fullscreen view while the next one played,
  // unseen, behind it.
  function goToNextItem(item, ctx) {
    const next = item.nextElementSibling;
    if (!next || !next.classList.contains("litelok-gallery__item")) return;
    const root = fsIntentFor(item);
    const intent = fsIntent.get(root);
    noteRowNavigation(next);
    next.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    playItem(next, ctx);
    // Desktop container fullscreen only — re-requesting it here is still
    // inside the same user-gesture chain (the "Play now"/fs-next button
    // click that got us here), so browsers allow it. Mobile's native
    // <video>-level fullscreen ("video" intent) deliberately does NOT try
    // this: by the time we're here it's an "ended" event or a timer
    // firing, neither of which counts as a user gesture, so the request
    // would just silently fail — see the "ended" handler in wireNextUp,
    // which already dropped back to inline view before calling this.
    if (intent === "container") {
      const nextMedia = next.querySelector(".litelok-gallery__media");
      if (nextMedia?.requestFullscreen) nextMedia.requestFullscreen().catch(() => {});
      else if (nextMedia?.webkitRequestFullscreen) nextMedia.webkitRequestFullscreen();
    }
  }

  // Visible only while its card is the fullscreen element (CSS gates it on
  // :fullscreen) — a plain "next video" chevron, since there's no
  // countdown to show here, just a manual skip.
  function addFullscreenNextButton(media, item, ctx) {
    const next = item.nextElementSibling;
    if (!next || !next.classList.contains("litelok-gallery__item")) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "litelok-gallery__fs-next";
    btn.setAttribute("aria-label", "Next video");
    btn.innerHTML =
      '<svg width="14" height="22" viewBox="0 0 14 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L12 11L1 21" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      goToNextItem(item, ctx);
    });
    media.appendChild(btn);
  }

  // Mirrors goToNextItem, but going backwards. The previous item was
  // already played once, so it already has a real <video> sitting in its
  // media (playItem() would just no-op on it since dataset.opened is
  // already set) — reuse that element and restart it from 0 rather than
  // trying to recreate it. Falls back to playItem() for the edge case
  // where the previous item was somehow never opened.
  function goToPreviousItem(item, ctx) {
    const prev = item.previousElementSibling;
    if (!prev || !prev.classList.contains("litelok-gallery__item")) return;
    const root = fsIntentFor(item);
    const intent = fsIntent.get(root);
    noteRowNavigation(prev);
    prev.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });

    const prevMedia = prev.querySelector(".litelok-gallery__media");
    let prevVideo = prevMedia ? prevMedia.querySelector("video") : null;
    if (prevVideo) {
      prevVideo.currentTime = 0;
      prevVideo.play().catch(() => {});
    } else {
      playItem(prev, ctx);
      prevVideo = prevMedia ? prevMedia.querySelector("video") : null;
    }

    // Desktop-only in practice (the prev button that calls this is never
    // rendered on mobile — see playItem), and always inside that button's
    // own click handler, so re-requesting container fullscreen here is
    // still within the user gesture. No "video" case needed for the same
    // reason goToNextItem no longer has one.
    if (intent === "container") {
      if (prevMedia?.requestFullscreen) prevMedia.requestFullscreen().catch(() => {});
      else if (prevMedia?.webkitRequestFullscreen) prevMedia.webkitRequestFullscreen();
    }
  }

  // Desktop-fullscreen-only "previous video" chevron, symmetric to the
  // next button — simply not created when there's nothing before this
  // item in the row.
  function addFullscreenPrevButton(media, item, ctx) {
    const prev = item.previousElementSibling;
    if (!prev || !prev.classList.contains("litelok-gallery__item")) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "litelok-gallery__fs-prev";
    btn.setAttribute("aria-label", "Previous video");
    btn.innerHTML =
      '<svg width="14" height="22" viewBox="0 0 14 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 1L2 11L13 21" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      goToPreviousItem(item, ctx);
    });
    media.appendChild(btn);
  }

  // "Next video in..." — shown in the last 10s of a native <video>'s
  // playback, counting down to auto-advance into the next item in the
  // row. Only wired for native <video> (the uploaded-video path): a
  // YouTube/Vimeo iframe with no player SDK attached can't report real
  // playback position, so embeds don't get this yet — would need each
  // platform's JS SDK loaded, a bigger follow-up piece.
  function wireNextUp(item, video, ctx) {
    const next = item.nextElementSibling;
    if (!next || !next.classList.contains("litelok-gallery__item")) return;

    let cancelled = false;
    let advanced = false;
    let overlay = null;

    function goToNext() {
      advanced = true;
      if (overlay) {
        overlay.remove();
        overlay = null;
      }
      goToNextItem(item, ctx);
    }

    function ensureOverlay() {
      if (overlay) return overlay;
      overlay = document.createElement("div");
      overlay.className = "litelok-gallery__next-up";
      const text = document.createElement("span");
      const actions = document.createElement("div");
      actions.className = "litelok-gallery__next-up-actions";
      const playNowBtn = document.createElement("button");
      playNowBtn.type = "button";
      playNowBtn.className = "litelok-gallery__next-up-play";
      playNowBtn.textContent = "Play now";
      playNowBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!cancelled && !advanced) goToNext();
      });
      const cancelBtn = document.createElement("button");
      cancelBtn.type = "button";
      cancelBtn.className = "litelok-gallery__next-up-cancel";
      cancelBtn.textContent = "Cancel";
      cancelBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        cancelled = true;
        overlay.remove();
        overlay = null;
      });
      actions.appendChild(playNowBtn);
      actions.appendChild(cancelBtn);
      overlay.appendChild(text);
      overlay.appendChild(actions);
      const media = item.querySelector(".litelok-gallery__media");
      if (media) media.appendChild(overlay);
      return overlay;
    }

    // Mobile native video fullscreen only lets this overlay exist in the
    // DOM, not be seen — a <video> in native fullscreen has no room for
    // custom HTML on top of it. So rather than wait for the video to end
    // (by which point there's nothing left to show a countdown for),
    // proactively drop out of fullscreen the moment the countdown would
    // start (remaining <= 10s), same trigger desktop already uses. Unlike
    // *entering* fullscreen, *exiting* it isn't gated behind a user
    // gesture — this works called from a plain timeupdate handler. The
    // video keeps playing inline (playsInline is already set), now at
    // its normal in-row size, with the countdown visible over it exactly
    // like the desktop/non-fullscreen case — then "ended" advances
    // normally, no special-casing needed.
    let exitedFullscreenForCountdown = false;
    function exitMobileFullscreenIfNeeded() {
      if (exitedFullscreenForCountdown) return;
      const root = fsIntentFor(item);
      if (fsIntent.get(root) !== "video") return;
      // fsIntent is tracked per row, not per video, so it carries across
      // items on purpose (see goToNextItem) — but that means if an
      // EARLIER video in this same row was fullscreened and the flag was
      // never cleared, it stays "video" for the rest of the page session
      // and wrongly fires here for a later video that was never
      // fullscreen at all. Reported directly: the countdown paused
      // playback on a video watched entirely inline, no fullscreen
      // involved. Only actually exiting fullscreen (and so only actually
      // needing to pause for it) when something is genuinely fullscreen
      // right now — otherwise this is stale, so drop it instead of
      // acting on it.
      const actuallyFullscreen =
        document.fullscreenElement || document.webkitFullscreenElement || video.webkitDisplayingFullscreen;
      if (!actuallyFullscreen) {
        fsIntent.delete(root);
        return;
      }
      exitedFullscreenForCountdown = true;
      // Exiting native video fullscreen pauses playback as a side effect
      // on mobile Safari/Chrome (confirmed by testing: the countdown
      // showed correctly, but the video itself had silently stopped,
      // which meant "ended" never fired and nothing ever advanced).
      // Resume right after — on the fullscreen-exit event where
      // available, plus a short timeout fallback in case that event
      // doesn't fire reliably for every browser/version. Guarded by
      // cancelled/advanced so a deliberate Cancel-while-exiting doesn't
      // get overridden by a resume that was already in flight.
      function resumeIfNeeded() {
        if (cancelled || advanced) return;
        if (video.paused) video.play().catch(() => {});
      }
      video.addEventListener("webkitendfullscreen", resumeIfNeeded, { once: true });
      document.addEventListener("fullscreenchange", resumeIfNeeded, { once: true });
      setTimeout(resumeIfNeeded, 300);
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (video.webkitExitFullscreen) video.webkitExitFullscreen();
    }

    video.addEventListener("timeupdate", () => {
      if (cancelled || advanced || !isFinite(video.duration)) return;
      const remaining = video.duration - video.currentTime;
      if (remaining <= 10 && remaining > 0.25) {
        exitMobileFullscreenIfNeeded();
        const el = ensureOverlay();
        el.firstChild.textContent = `Next video in ${Math.ceil(remaining)}s`;
      } else if (overlay) {
        overlay.remove();
        overlay = null;
      }
    });

    video.addEventListener("ended", () => {
      if (cancelled || advanced) return;
      goToNext();
    });
  }

  // Touch devices (phones/tablets) only — desktop keeps the browser's
  // native controls exactly as before.
  function usesTouchControls() {
    return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  }

  const PLAY_ICON =
    '<svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 13L0.75 25.2583L0.75 0.741701L21 13Z" fill="currentColor"/></svg>';
  const PAUSE_ICON =
    '<svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="6" height="22" rx="1.5" fill="currentColor"/><rect x="13" y="1" width="6" height="22" rx="1.5" fill="currentColor"/></svg>';

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const s = Math.floor(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = String(s % 60).padStart(2, "0");
    return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
  }

  // YouTube-style controls for touch devices, replacing the native ones.
  // iOS's native inline controls tint the whole video dark whenever they're
  // showing, and show for ~3s every time playback starts or resumes — so
  // the first tap-to-play and every resume came with a dark overlay. These
  // instead: hidden when playback starts or resumes; one tap on a playing
  // video shows them (a centre play/pause button, time, a seek bar along
  // the bottom edge, the fullscreen button), another tap or ~2.5s hides
  // them again; they stay up while paused so there's always a way back
  // in. No full-frame dim — just a light gradient behind the bottom row so
  // the time stays readable.
  //
  // Everything here only changes opacity/text inside the card's own
  // (contain: layout) media box, so it can't move or resize the row.
  function addTouchControls(media, video) {
    const HIDE_AFTER_MS = 2500;
    media.dataset.touchControls = "true";
    media.dataset.controls = "hidden";
    media.dataset.buffering = "true";

    const layer = document.createElement("div");
    layer.className = "litelok-gallery__controls";
    layer.innerHTML =
      '<div class="litelok-gallery__spinner" aria-hidden="true"></div>' +
      `<button type="button" class="litelok-gallery__toggle" aria-label="Pause">${PAUSE_ICON}</button>` +
      '<span class="litelok-gallery__time">0:00 / 0:00</span>' +
      '<div class="litelok-gallery__seek" role="slider" aria-label="Seek" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">' +
      '<div class="litelok-gallery__seek-fill"></div><div class="litelok-gallery__seek-handle"></div></div>';
    media.appendChild(layer);
    const toggle = layer.querySelector(".litelok-gallery__toggle");
    const time = layer.querySelector(".litelok-gallery__time");
    const seek = layer.querySelector(".litelok-gallery__seek");
    const fill = layer.querySelector(".litelok-gallery__seek-fill");
    const handle = layer.querySelector(".litelok-gallery__seek-handle");

    let hideTimer = null;
    let scrubbing = false;

    function setShown(shown) {
      clearTimeout(hideTimer);
      media.dataset.controls = shown ? "shown" : "hidden";
      if (shown && !video.paused && !scrubbing) hideTimer = setTimeout(() => setShown(false), HIDE_AFTER_MS);
    }

    function render() {
      const duration = video.duration;
      const ratio = isFinite(duration) && duration > 0 ? Math.min(video.currentTime / duration, 1) : 0;
      const label = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
      if (time.textContent !== label) time.textContent = label;
      fill.style.transform = `scaleX(${ratio})`;
      // Handle centre runs from 6px to width-6px (its radius), so it's
      // never half off the side of the card at the very start or end.
      handle.style.left = `calc(6px + (100% - 12px) * ${ratio})`;
      seek.setAttribute("aria-valuenow", String(Math.round(ratio * 100)));
    }

    function syncPlayState() {
      toggle.innerHTML = video.paused ? PLAY_ICON : PAUSE_ICON;
      toggle.setAttribute("aria-label", video.paused ? "Play" : "Pause");
    }

    video.addEventListener("play", () => {
      syncPlayState();
      setShown(false);
    });
    video.addEventListener("pause", () => {
      syncPlayState();
      delete media.dataset.buffering;
      setShown(true);
    });
    video.addEventListener("waiting", () => {
      media.dataset.buffering = "true";
    });
    video.addEventListener("playing", () => {
      delete media.dataset.buffering;
    });
    ["timeupdate", "durationchange", "loadedmetadata", "seeked"].forEach((type) => video.addEventListener(type, render));

    toggle.addEventListener("click", (e) => {
      e.stopPropagation();
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });

    // Tapping the picture: while paused, plays (like the poster did);
    // while playing, shows/hides the controls rather than pausing.
    layer.addEventListener("click", (e) => {
      if (seek.contains(e.target)) return;
      if (video.paused) video.play().catch(() => {});
      else setShown(media.dataset.controls !== "shown");
    });

    // Seek bar: touch-action: none (CSS) so dragging along it scrubs
    // instead of swiping the row.
    function seekTo(clientX) {
      const rect = seek.getBoundingClientRect();
      const ratio = rect.width > 0 ? Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1) : 0;
      if (isFinite(video.duration)) video.currentTime = ratio * video.duration;
      render();
    }
    seek.addEventListener("pointerdown", (e) => {
      scrubbing = true;
      setShown(true);
      seek.setPointerCapture(e.pointerId);
      seekTo(e.clientX);
    });
    seek.addEventListener("pointermove", (e) => {
      if (scrubbing) seekTo(e.clientX);
    });
    function endScrub() {
      if (!scrubbing) return;
      scrubbing = false;
      setShown(true);
    }
    seek.addEventListener("pointerup", endScrub);
    seek.addEventListener("pointercancel", endScrub);

    render();
    return {
      // Playback was refused (e.g. iOS Low Power Mode): drop the spinner
      // and offer the play button instead.
      showPaused() {
        delete media.dataset.buffering;
        syncPlayState();
        setShown(true);
      },
    };
  }

  // Click-to-play: swaps the poster/button for the real element and
  // starts playback. Callable from a click, or programmatically for
  // auto-advance into the next item.
  function playItem(item, ctx) {
    if (item.dataset.opened) return;
    const media = item.querySelector(".litelok-gallery__media");
    const embedType = item.dataset.embedType;
    const embedSrc = item.dataset.embedSrc;
    if (!media || !embedSrc) return;

    pauseOtherPlayingItems(item.closest("[data-litelok-gallery]"), item);
    markOpen(item, ctx);

    if (embedType === "video") {
      const video = document.createElement("video");
      video.src = embedSrc;
      // Touch devices get our own lightweight controls instead of the
      // browser's (see addTouchControls). The poster stays up as the
      // video's own poster until the first frame, rather than black.
      const touchControls = usesTouchControls();
      video.controls = !touchControls;
      video.controlsList = "nofullscreen nodownload";
      video.autoplay = true;
      video.playsInline = true;
      const poster = media.querySelector(".litelok-gallery__poster");
      if (touchControls && poster) video.poster = poster.currentSrc || poster.src;
      media.replaceChildren(video);
      let touchUi = null;
      video.play().catch(() => {
        if (touchUi) touchUi.showPaused();
      });
      if (touchControls) touchUi = addTouchControls(media, video);
      wireAnalytics(item, video, "video", { ...ctx, getVideoId: () => item.dataset.videoId });
      wireNextUp(item, video, ctx);
      addFullscreenButton(media, video, item);
      // Only on desktop — mobile fullscreen targets the <video> element
      // itself (see addFullscreenButton), which can't host these buttons.
      if (window.innerWidth >= 750) {
        addFullscreenNextButton(media, item, ctx);
        addFullscreenPrevButton(media, item, ctx);
      }
    } else {
      const iframe = document.createElement("iframe");
      const sep = embedSrc.includes("?") ? "&" : "?";
      // enablejsapi/api unlock each platform's postMessage control surface
      // (pauseOtherPlayingItems above needs this to stop a backgrounded
      // embed when a different item starts playing) — harmless to request
      // even for a viewer who never triggers that path.
      const apiParam = item.dataset.provider === "youtube" ? "enablejsapi=1" : item.dataset.provider === "vimeo" ? "api=1" : "";
      iframe.src = `${embedSrc}${sep}autoplay=1${apiParam ? "&" + apiParam : ""}`;
      iframe.title = item.dataset.title || "Video";
      iframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
      iframe.allowFullscreen = true;
      iframe.setAttribute("webkitallowfullscreen", "");
      iframe.setAttribute("mozallowfullscreen", "");
      iframe.loading = "lazy";
      media.replaceChildren(iframe);
      addIframeFullscreenButton(media, iframe);
    }
  }

  // Click-anywhere-to-play/pause: before playback starts, the whole
  // poster is the play target, not just the small button drawn on top of
  // it. Once playing, clicking the open video area toggles play/pause —
  // browsers don't do this on their own for a plain <video controls>
  // (only the small button *inside* the controls bar toggles it
  // natively); this adds the same "tap anywhere" behaviour YouTube/
  // Netflix have. A ~40px strip at the bottom is deliberately left alone
  // so this doesn't fight the native controls bar's own click handling
  // (its scrubber/volume/play-button clicks still bubble up here — acting
  // on them too would double-toggle).
  //
  // Bubble-phase click, deciding play vs pause from video.paused captured
  // on the preceding pointerdown rather than read live inside the click
  // handler. Mobile browsers draw their own big "paused" play icon over
  // the video with its own native tap handling (not a separate DOM node
  // we can target), which can resume playback natively before this click
  // handler even runs — reading video.paused live at that point sees the
  // state *after* that native resume and immediately pauses it right back
  // (looks like the play button does nothing). pointerdown always fires
  // before any click-triggered native action, so the paused/playing state
  // captured there reflects what was true before the tap did anything;
  // acting on that instead of the live value avoids the race without
  // needing preventDefault or the capture phase (an earlier attempt using
  // those broke the very first click-to-play, since capture fires before
  // the click reaches its target at all — see the removed comment history
  // in git for that dead end). Only wired for native <video> — an iframe
  // embed has no toggle API to call.
  function initPlayTriggers(root, ctx) {
    root.querySelectorAll("[data-litelok-video]").forEach((item) => {
      const media = item.querySelector(".litelok-gallery__media");
      if (!media) return;
      let pointerDownPaused = null;
      media.addEventListener("pointerdown", () => {
        const video = media.querySelector("video");
        pointerDownPaused = video ? video.paused : null;
      });
      media.addEventListener("click", (e) => {
        if (!item.dataset.opened) {
          playItem(item, ctx);
          return;
        }
        // Touch controls handle their own taps (see addTouchControls).
        if (media.dataset.touchControls) return;
        const video = media.querySelector("video");
        if (!video) return;
        const rect = video.getBoundingClientRect();
        if (e.clientY > rect.bottom - 40) return;
        const shouldPlay = pointerDownPaused === null ? video.paused : pointerDownPaused;
        if (shouldPlay) video.play();
        else video.pause();
      });
    });
  }

  // Hover preview (desktop only) — hovering an unplayed poster for a
  // moment starts a silent, looping preview over it, same as YouTube's
  // thumbnail hover. Skipped entirely on touch devices via (hover: none).
  // A short delay (350ms) avoids starting a preview for every card the
  // mouse merely passes over. Only wired for native <video> items.
  // Doesn't touch item.dataset.opened/markOpen: a hover preview isn't a
  // real "view" for analytics purposes, only an actual click is.
  //
  // One shared preview <video> per row, moved into whichever card is
  // currently hovered, rather than a separate element per card. Giving
  // every card its own preview element meant quickly moving the mouse
  // across several cards fired off that many concurrent first-time
  // connections to the CDN at once — mobile/desktop browsers only allow a
  // handful of concurrent connections per host, so most of those requests
  // sat half-finished competing for a slot, which is exactly what showed
  // up as "laggy, as if it hasn't had time to buffer" (and why walking
  // backwards from a later card, where each connection had already had
  // time to establish on its own, loaded instantly). A single shared
  // element guarantees only one preview is ever loading at a time.
  function initHoverPreview(root) {
    if (window.matchMedia("(hover: none)").matches) return;
    const items = [...root.querySelectorAll("[data-litelok-video]")].filter(
      (item) => item.dataset.embedType === "video"
    );
    if (!items.length) return;

    const previewVideo = document.createElement("video");
    previewVideo.className = "litelok-gallery__preview";
    previewVideo.muted = true;
    previewVideo.loop = true;
    previewVideo.playsInline = true;

    let hoverTimer = null;
    let hideTimer = null;
    let currentItem = null;

    function detach() {
      previewVideo.pause();
      if (previewVideo.parentNode) previewVideo.remove();
      currentItem = null;
    }

    items.forEach((item) => {
      const media = item.querySelector(".litelok-gallery__media");
      const poster = media?.querySelector(".litelok-gallery__poster");
      if (!media || !poster) return;

      media.addEventListener("mouseenter", () => {
        if (item.dataset.opened) return;
        clearTimeout(hideTimer);
        hideTimer = null;
        hoverTimer = setTimeout(() => {
          if (item.dataset.opened) return;
          if (currentItem !== item) {
            currentItem = item;
            media.appendChild(previewVideo);
            // Only reset src (and so only pay the connection-setup cost
            // again) when actually switching to a different video — not
            // on every re-hover of the same card.
            if (previewVideo.dataset.forItem !== item.dataset.embedSrc) {
              previewVideo.dataset.forItem = item.dataset.embedSrc;
              previewVideo.src = item.dataset.embedSrc;
            }
          }
          previewVideo.play().catch(() => {});
        }, 350);
      });

      media.addEventListener("mouseleave", () => {
        clearTimeout(hoverTimer);
        hoverTimer = null;
        if (currentItem !== item) return;
        previewVideo.pause();
        // Grace period before detaching — a quick pass back over the same
        // card (or a neighbour, handled by the mouseenter above) shouldn't
        // throw away a connection that's still warm.
        hideTimer = setTimeout(() => {
          if (currentItem === item) detach();
        }, 2000);
      });
    });
  }

  // Mobile equivalent of the hover preview above — there's no cursor to
  // hover with on a touch device, so the signal for "this is the one
  // being looked at" becomes scroll position instead. Gated to touch
  // devices via (hover: none) so this never doubles up with the desktop
  // hover version.
  //
  // Same shared-element approach as initHoverPreview above, for the same
  // reason: this used to create one <video> per card up front and toggle
  // its src as each one scrolled through the 0.5 threshold, which meant a
  // normal-speed scroll past several cards had that many concurrent
  // first-time connections competing for a mobile browser's small
  // per-host connection pool — reported directly as inconsistent (a
  // different set of cards failing to preview every reload) and, after an
  // earlier attempt just delayed tearing those connections down, as not
  // previewing "at all" — delaying the teardown didn't reduce how many
  // connections were open at once during a scroll, it just kept the
  // failed ones around longer. A single shared element removes the
  // contention at the source: only ever one request in flight, moved into
  // whichever card is currently most in view.
  function initMobilePreview(root) {
    if (!window.matchMedia("(hover: none)").matches) return;
    const items = [...root.querySelectorAll("[data-litelok-video]")].filter(
      (item) => item.dataset.embedType === "video"
    );
    if (!items.length) return;

    const previewVideo = document.createElement("video");
    previewVideo.className = "litelok-gallery__preview";
    previewVideo.muted = true;
    previewVideo.loop = true;
    previewVideo.playsInline = true;

    let currentItem = null;

    const visibilityIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const item = entry.target;
          if (item.dataset.opened) {
            visibilityIO.unobserve(item);
            if (currentItem === item) {
              previewVideo.pause();
              if (previewVideo.parentNode) previewVideo.remove();
              currentItem = null;
            }
            return;
          }
          if (!entry.isIntersecting) {
            if (currentItem === item) {
              previewVideo.pause();
            }
            return;
          }
          // Most-visible candidate among current entries — during a fast
          // scroll, more than one card can cross the threshold in the
          // same callback; only ever attach to the one actually centred.
          if (entry.intersectionRatio < 0.6) return;
          if (currentItem !== item) {
            currentItem = item;
            const media = item.querySelector(".litelok-gallery__media");
            if (!media) return;
            media.appendChild(previewVideo);
            if (previewVideo.dataset.forItem !== item.dataset.embedSrc) {
              previewVideo.dataset.forItem = item.dataset.embedSrc;
              previewVideo.src = item.dataset.embedSrc;
            }
          }
          previewVideo.play().catch(() => {});
        });
      },
      { threshold: [0.6] }
    );

    items.forEach((item) => visibilityIO.observe(item));
  }

  // Pauses a card's *real* (clicked-to-play) video once it's scrolled
  // mostly out of view — playIO above only manages the silent muted
  // preview and explicitly stops watching an item the moment it's opened
  // (see the `item.dataset.opened` branch there), so without this,
  // scrolling from a genuinely playing video straight to the next one left
  // the first one still audibly/visibly playing behind the scenes with no
  // way to get back to it except scrolling back. Deliberately doesn't
  // resume it on scrolling back — that would fight a deliberate pause —
  // it just leaves it paused, same as tapping pause yourself.
  //
  // Was mobile-only at first (the `hover: none` gate below has been
  // removed) — a played video scrolled out of view on a tall product page
  // is exactly as easy to leave running in the background on a mouse/
  // trackpad device as on a touch one, so this now applies everywhere.
  //
  // A diagnostic pass briefly skipped this for the mobile-scroll row, to
  // test whether video.pause() itself was triggering the "previous card
  // jumps in" glitch (iOS Safari has quirks where pausing a video can
  // trigger a "bring media into view" correction). Confirmed by direct
  // testing: the glitch still happens with the video playing
  // continuously, un-paused — ruling this out. Leaving it un-paused also
  // surfaced a real, separate problem: the video's own visual frame goes
  // black while scrolled off-screen (audio keeps playing) once it's been
  // off-screen long enough, a known mobile-browser behavior for a video
  // that isn't visible — worse than the glitch itself. Restored to
  // pausing everywhere, same as before that diagnostic.
  function initPauseOnScroll(root) {
    const pauseIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) return;
          if (!entry.target.dataset.opened) return;
          const video = entry.target.querySelector(".litelok-gallery__media video");
          if (video && !video.paused) {
            notePauseReason(video, "scrolled_away");
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    root.querySelectorAll("[data-litelok-video]").forEach((item) => pauseIO.observe(item));
  }

  // Called before our own code scrolls the row to a card (next/previous,
  // auto-advance at the end of a video), so that move counts as intended.
  function noteRowNavigation(item) {
    const navigate = rowNavigators.get(item.parentElement);
    if (navigate) navigate(item);
  }

  // Keeps the mobile row resting on the card the viewer chose. The root
  // cause of the scroll-back glitch is fixed in applyFullBleed (see the
  // comment there); this is the net for anything else a browser might do
  // to the row's scroll position on its own.
  //
  // Replaces the old "once scrolling settles, snap to the NEAREST card"
  // correction. Nearest-card only fixed small drifts — once the row had
  // been pulled more than halfway, "nearest" WAS the previous card, so the
  // correction finished the glitch instead of undoing it. Now the row
  // remembers which card it last came to rest on after a real interaction
  // (touch, click, wheel, keyboard, focus, or our own next/previous/auto-
  // advance), and:
  //
  //  - after an interaction: once it settles, that card becomes the
  //    remembered one — the same result the old nearest-card snap gave,
  //    so swiping is untouched (native scroll + native snap, passive
  //    listeners only, nothing is ever prevented or driven from JS);
  //  - with no interaction, while the page itself is scrolling or
  //    resizing (the glitch's exact signature): once it settles, put it
  //    back on the remembered card rather than adopting wherever the
  //    browser left it;
  //  - with no interaction and the page still: falls back to the old
  //    nearest-card behaviour, so anything that scrolls the row without
  //    an event we can see (e.g. screen-reader navigation) still wins;
  //  - when the row's own width changes (rotation, desktop resize), keep
  //    the same card in view instead of the same pixel offset.
  //
  // Unlike the reverted "reopen realign" attempt, nothing here reacts to
  // the row becoming visible again or to which video was last opened: it
  // only acts after the row has actually moved (or changed width), after
  // a 150ms settle, and toward the card the viewer last left it on.
  function initRowPosition(root) {
    const wrap = root.querySelector("[data-litelok-gallery-scroll]");
    if (!wrap) return;
    const grid = wrap.querySelector('.litelok-gallery__grid[data-mobile-scroll="true"]');
    if (!grid) return;
    const items = [...grid.querySelectorAll(".litelok-gallery__item")];
    if (!items.length) return;

    // How recently the page must have scrolled/resized for a row movement
    // nobody asked for to count as the glitch rather than something else.
    const PAGE_MOVE_WINDOW_MS = 1000;

    let current = nearestIndex();
    let touching = false;
    let interacted = false;
    let movedWithPage = false;
    let lastPageMove = -Infinity;
    let settleTimer = null;

    function nearestIndex() {
      let best = 0;
      for (let i = 1; i < items.length; i++) {
        if (Math.abs(items[i].offsetLeft - grid.scrollLeft) < Math.abs(items[best].offsetLeft - grid.scrollLeft)) best = i;
      }
      return best;
    }

    function alignTo(index) {
      const target = items[index];
      if (target && Math.abs(grid.scrollLeft - target.offsetLeft) > 1) grid.scrollLeft = target.offsetLeft;
    }

    function settle() {
      settleTimer = null;
      if (touching) return; // finger still down — touchend re-arms this
      if (interacted || !movedWithPage) current = nearestIndex();
      interacted = false;
      movedWithPage = false;
      alignTo(current);
    }

    function armSettle() {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 150);
    }

    function markInteraction() {
      interacted = true;
      armSettle();
    }

    grid.addEventListener(
      "scroll",
      () => {
        if (!interacted && !touching && performance.now() - lastPageMove < PAGE_MOVE_WINDOW_MS) movedWithPage = true;
        armSettle();
      },
      { passive: true }
    );

    function endTouch(e) {
      if (!touching || e.touches.length) return;
      touching = false;
      armSettle();
    }
    document.addEventListener("touchend", endTouch, { capture: true, passive: true });
    document.addEventListener("touchcancel", endTouch, { capture: true, passive: true });
    // On the wrap, not just the grid, so the desktop arrows/scrollbar count
    // too. All passive: these only observe, they never touch the gesture.
    wrap.addEventListener(
      "touchstart",
      (e) => {
        touching = true;
        markInteraction();
        // touchend always goes to the element the touch started on — if
        // that's been removed from the page since (e.g. the countdown
        // overlay), it never reaches document, so listen on it directly.
        e.target.addEventListener("touchend", endTouch, { once: true, passive: true });
        e.target.addEventListener("touchcancel", endTouch, { once: true, passive: true });
      },
      { passive: true }
    );
    ["pointerdown", "wheel", "keydown", "focusin"].forEach((type) =>
      wrap.addEventListener(type, markInteraction, { passive: true })
    );

    function notePageMove() {
      lastPageMove = performance.now();
    }
    window.addEventListener("scroll", notePageMove, { passive: true });
    window.addEventListener("resize", notePageMove, { passive: true });

    rowNavigators.set(grid, (item) => {
      const index = items.indexOf(item);
      if (index === -1) return;
      current = index;
      markInteraction();
    });

    if ("ResizeObserver" in window) {
      let lastWidth = grid.clientWidth;
      new ResizeObserver(() => {
        const width = grid.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        if (!touching) alignTo(current);
      }).observe(grid);
    }
  }

  // Full-bleed — mobile at 1 video per row, or desktop when the merchant
  // opts in via the "Full-bleed on desktop" checkbox — measured at runtime
  // rather than guessed in CSS. Two CSS-only attempts (a generic
  // 50vw-centered guess, then this theme's own --container-outer-width
  // variable) both left a visible gap when this was first built for
  // mobile, meaning neither assumption matched how this theme's real,
  // currently-rendered DOM/CSS actually resolves it. This sidesteps
  // needing to know why: reset any inline override, measure the real gap
  // between the element and the true viewport edges via
  // getBoundingClientRect(), then cancel out exactly that with negative
  // margins — correct regardless of what's producing the gap. Re-runs on
  // resize/rotation.
  //
  // One function handling both breakpoints, not two independent ones —
  // they'd fight over the same root.style.marginLeft/-Right on every
  // resize (each recomputing independently, last one to run wins,
  // clobbering the other's result) if wired as separate listeners on the
  // same element.
  //
  // v2 — this was the root cause of the mobile "previous video pulls into
  // frame after scrolling the page away and back" glitch. The old version
  // re-ran on EVERY window "resize" event, and iOS Safari fires "resize"
  // during ordinary vertical page scrolling: each time its toolbar
  // collapses (scrolling down) or comes back (scrolling up). Each re-run
  // reset both margins to 0px, forced a layout to measure, then put the
  // negative margins back — so for that one intermediate layout the row
  // was narrower by the theme's page gutters (~40px on a phone), and every
  // card's snap position moved left by (card index x 40px). Card 1's is 0
  // at any width, which is why video 1 was never affected. Browsers
  // re-snap a scroll-snap row during layout, so the row genuinely moved
  // there — measured on a 390px viewport: 406 -> 366, 812 -> 732,
  // 1218 -> 1098 for cards 2/3/4, i.e. the previous card ~10/20/31% into
  // frame. Chrome then re-snaps to the same card once the margins return
  // (so no Chromium test ever showed it); Safari tracks snap positions,
  // not cards, and could be left there or finish the move onto the
  // previous card. Whether a given scroll happened to toggle the toolbar
  // is the "identical actions, different result" randomness.
  //
  // Fixed at the source: only re-measure when a width the measurement
  // depends on actually changes — the layout viewport's, or the
  // container the gallery sits in (e.g. if it was hidden at load). A
  // height-only resize (the iOS toolbar, the on-screen keyboard) can't
  // change these margins, so it no longer touches the row at all.
  // documentElement.clientWidth is the layout viewport width — unaffected
  // by the toolbar and by pinch-zoom (Safari's innerWidth follows the
  // zoomed visual viewport). A genuine width change (rotation, desktop
  // window resize) still re-measures the original way, since the row is
  // being re-laid out then anyway, and initRowPosition keeps the same card
  // in view through it. (Measuring without the 0px reset was tried: on
  // mobile, the page's own overflow from the old margins can widen the
  // viewport being measured against, so it never corrects.)
  function applyFullBleed(root) {
    const mobileEnabled = root.dataset.mobileCols === "1";
    const desktopEnabled = root.dataset.desktopFullBleed === "true";
    if (!mobileEnabled && !desktopEnabled) return;
    const container = root.parentElement;
    let measuredFor = null;
    function apply() {
      // Not rendered (inside something hidden) — nothing real to measure;
      // the container observer below re-runs this once it's shown.
      if (!root.getClientRects().length) return;
      // The container's width doesn't depend on root's own margins.
      const key = `${document.documentElement.clientWidth}:${container ? container.clientWidth : 0}`;
      if (key === measuredFor) return;
      measuredFor = key;
      const isDesktop = window.innerWidth >= 750;
      const active = isDesktop ? desktopEnabled : mobileEnabled;
      if (!active) {
        root.style.marginLeft = "";
        root.style.marginRight = "";
        return;
      }
      root.style.marginLeft = "0px";
      root.style.marginRight = "0px";
      const rect = root.getBoundingClientRect();
      root.style.marginLeft = `${-rect.left}px`;
      root.style.marginRight = `${rect.right - window.innerWidth}px`;
    }
    apply();
    window.addEventListener("resize", apply);
    if (container && "ResizeObserver" in window) new ResizeObserver(apply).observe(container);
  }

  // Carousel controls for rows that overflow their configured column
  // count (data-mobile-scroll / data-desktop-scroll="true"): keeps the
  // edge-fade opacity and arrow visibility in sync with real scroll
  // position, and drives a custom scrollbar (click-to-seek + drag) since
  // the native one is hidden in CSS in favour of this slimmer look.
  function initCarouselControls(root) {
    const wrap = root.querySelector("[data-litelok-gallery-scroll]");
    if (!wrap) return;
    const grid = wrap.querySelector(".litelok-gallery__grid");
    const prev = wrap.querySelector("[data-litelok-gallery-prev]");
    const next = wrap.querySelector("[data-litelok-gallery-next]");
    const track = wrap.querySelector("[data-litelok-gallery-scrollbar]");
    const thumb = wrap.querySelector("[data-litelok-gallery-scrollbar-thumb]");
    if (!grid) return;

    function updateEdges() {
      const canScrollLeft = grid.scrollLeft > 4;
      const canScrollRight = grid.scrollLeft + grid.clientWidth < grid.scrollWidth - 4;
      wrap.dataset.canScrollLeft = String(canScrollLeft);
      wrap.dataset.canScrollRight = String(canScrollRight);
      if (prev) prev.hidden = !canScrollLeft;
      if (next) next.hidden = !canScrollRight;
    }

    function scrollByOneItem(direction) {
      const item = grid.querySelector(".litelok-gallery__item");
      const amount = item ? item.getBoundingClientRect().width + 16 : grid.clientWidth * 0.8;
      grid.scrollBy({ left: direction * amount, behavior: "smooth" });
    }

    grid.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    if (prev) prev.addEventListener("click", () => scrollByOneItem(-1));
    if (next) next.addEventListener("click", () => scrollByOneItem(1));
    updateEdges();

    // Mobile dot indicators — one per item, built from the actual rendered
    // items rather than re-deriving a count from liquid, so it can never
    // drift out of sync with what's really in the row. Position is
    // approximated from scroll progress rather than measuring which item
    // is most visible: items are uniform width here (mobile is always a
    // single row of equal-share cards), so progress * (count - 1) lands on
    // the right index without needing a second observer.
    const dotsWrap = wrap.querySelector("[data-litelok-gallery-dots]");
    if (dotsWrap) {
      const dotItems = [...grid.querySelectorAll(".litelok-gallery__item")];
      dotsWrap.innerHTML = dotItems
        .map((_, i) => `<span class="litelok-gallery__dot" data-index="${i}"></span>`)
        .join("");
      const dots = [...dotsWrap.querySelectorAll(".litelok-gallery__dot")];
      function updateDots() {
        if (dots.length < 2) return;
        const maxScroll = grid.scrollWidth - grid.clientWidth;
        const progress = maxScroll > 0 ? grid.scrollLeft / maxScroll : 0;
        const activeIndex = Math.round(progress * (dots.length - 1));
        dots.forEach((dot, i) => {
          dot.dataset.active = String(i === activeIndex);
        });
      }
      grid.addEventListener("scroll", updateDots, { passive: true });
      window.addEventListener("resize", updateDots);
      updateDots();
    }

    if (!track || !thumb) return;

    function layoutThumb() {
      const ratio = grid.clientWidth / grid.scrollWidth;
      thumb.style.width = `${Math.max(ratio * 100, 8)}%`;
    }

    function updateThumb() {
      const maxScroll = grid.scrollWidth - grid.clientWidth;
      const progress = maxScroll > 0 ? grid.scrollLeft / maxScroll : 0;
      const trackWidth = track.clientWidth;
      const thumbWidth = thumb.offsetWidth;
      thumb.style.left = `${progress * (trackWidth - thumbWidth)}px`;
      track.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
    }

    function seekTo(clientX) {
      const rect = track.getBoundingClientRect();
      const thumbWidth = thumb.offsetWidth;
      const x = Math.min(Math.max(clientX - rect.left - thumbWidth / 2, 0), rect.width - thumbWidth);
      const usable = rect.width - thumbWidth;
      const progress = usable > 0 ? x / usable : 0;
      grid.scrollLeft = progress * (grid.scrollWidth - grid.clientWidth);
    }

    let dragging = false;
    track.addEventListener("pointerdown", (e) => {
      dragging = true;
      track.setPointerCapture(e.pointerId);
      seekTo(e.clientX);
    });
    track.addEventListener("pointermove", (e) => {
      if (dragging) seekTo(e.clientX);
    });
    track.addEventListener("pointerup", () => {
      dragging = false;
    });
    track.addEventListener("pointercancel", () => {
      dragging = false;
    });

    grid.addEventListener("scroll", updateThumb, { passive: true });
    window.addEventListener("resize", () => {
      layoutThumb();
      updateThumb();
    });
    layoutThumb();
    updateThumb();
  }

  async function initGallery(root) {
    if (root.dataset.litelokGalleryInitialized) return;
    root.dataset.litelokGalleryInitialized = "true";

    initCarouselControls(root);
    applyFullBleed(root);
    initHoverPreview(root);
    initMobilePreview(root);
    initPauseOnScroll(root);
    initRowPosition(root);

    const apiBase = root.dataset.apiBase;
    const sectionId = root.dataset.sectionId;
    const store = root.dataset.store || "uk";
    // Empty when the section isn't rendered on a product page (it has no
    // enabled_on template restriction) — omitted from the sync body below
    // rather than sent as "" so a stale cached widget without these fields
    // never has to be distinguished from "genuinely not on a product page".
    const productHandle = root.dataset.productHandle || undefined;
    const productTitle = root.dataset.productTitle || undefined;
    if (!apiBase || !sectionId) return;
    const ctx = { apiBase, sectionId, store };

    const items = [...root.querySelectorAll("[data-litelok-video]")];
    if (!items.length) return;

    initPlayTriggers(root, ctx);

    // Background sync — maps each already-rendered item to a real database
    // video id, needed so analytics beacons reference a real row. Purely
    // additive: if this fails, the videos stay visible and playable,
    // they just won't show up in the admin dashboard's numbers yet.
    let videoIds = [];
    try {
      const blocks = items.map((el) => ({
        title: el.dataset.title || el.querySelector(".litelok-gallery__caption")?.textContent || "Untitled",
        provider: el.dataset.provider,
        providerFileId: el.dataset.providerFileId,
        videoUrl:
          el.dataset.provider === "youtube"
            ? `https://youtu.be/${el.dataset.providerFileId}`
            : el.dataset.provider === "vimeo"
            ? `https://vimeo.com/${el.dataset.providerFileId}`
            : el.dataset.embedSrc || "",
        thumbnailUrl: el.querySelector(".litelok-gallery__poster")?.src || undefined,
      }));
      const res = await fetch(`${apiBase}/api/public/gallery-sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sectionId, store, style: "grid", blocks, productHandle, productTitle }),
      });
      const data = await res.json();
      videoIds = (data.videos || []).map((v) => v.id);
      if (data.placementId) root.dataset.placementId = data.placementId;
    } catch (e) {
      console.warn("Litelok Gallery: analytics sync failed (display unaffected)", e);
    }

    items.forEach((el, i) => (el.dataset.videoId = videoIds[i] || ""));

    // Impressions — each item counted once, the first time it's actually seen.
    const seen = new WeakSet();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !seen.has(entry.target)) {
            seen.add(entry.target);
            const videoId = entry.target.dataset.videoId;
            if (videoId) sendEvent(apiBase, videoId, "impression", sectionId, store);
          }
        });
      },
      { threshold: 0.5 }
    );
    items.forEach((el) => observer.observe(el));

    // Direct-embed Vimeo fallback (no custom thumbnail set, so the iframe
    // is already in the DOM at init rather than behind a play button) —
    // no reliable "played" event without the Vimeo Player SDK, so this is
    // logged as "open" on click same as before.
    items.forEach((el) => {
      const iframe = el.querySelector(".litelok-gallery__media--embed > iframe");
      if (iframe) el.addEventListener("click", () => markOpen(el, ctx));
    });
  }
})();